import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SlidersHorizontal, MapPin } from 'lucide-react';
import { DiscoveryCard } from './DiscoveryCard';
import type { DiscoveryListing, FeedPreferences, SwipeActionType } from '../types';

interface FeedContainerProps {
  preferences: FeedPreferences;
  onListingClick: (listing: DiscoveryListing) => void;
  onSave: (listing: DiscoveryListing) => void;
  onShare: (listing: DiscoveryListing) => void;
}

export function FeedContainer({
  preferences,
  onListingClick,
  onSave,
  onShare,
}: FeedContainerProps): JSX.Element {
  const [listings, setListings] = useState<DiscoveryListing[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Mock data loading
  useEffect(() => {
    loadMoreListings();
  }, [preferences]);

  const loadMoreListings = async () => {
    if (isLoading) return;
    setIsLoading(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500));

    const mockListings: DiscoveryListing[] = Array.from({ length: 10 }, (_, i) => ({
      id: `listing-${Date.now()}-${i}`,
      title: [
        'iPhone 14 Pro Max 256GB - Like New',
        'Toyota Corolla 2020 - Low Mileage',
        'MacBook Air M2 - Space Gray',
        'Sony PlayStation 5 - With Games',
        'Samsung 65" 4K Smart TV',
        'Honda Civic 2019 - Excellent Condition',
        'iPad Pro 12.9" - M1 Chip',
        'Nike Air Jordan 1 - Size 10',
        'Dell XPS 15 - i7 Processor',
        'Canon EOS R6 - Professional Camera',
      ][i],
      price: [185000, 3200000, 245000, 95000, 145000, 2800000, 195000, 45000, 185000, 420000][i],
      currency: 'PKR',
      location: ['Lahore', 'Karachi', 'Islamabad', 'Rawalpindi', 'Faisalabad'][Math.floor(Math.random() * 5)],
      images: [`https://picsum.photos/400/600?random=${i}`],
      category: ['Electronics', 'Vehicles', 'Electronics', 'Gaming', 'Electronics'][Math.floor(Math.random() * 5)],
      condition: ['New', 'Like New', 'Good', 'Fair'][Math.floor(Math.random() * 4)],
      description: 'Excellent condition, barely used. All accessories included.',
      seller: {
        id: `seller-${i}`,
        name: ['Ali Khan', 'Ahmed Hassan', 'Sara Malik', 'Usman Tariq', 'Fatima Ali'][Math.floor(Math.random() * 5)],
        avatar: `https://i.pravatar.cc/150?img=${i + 10}`,
        trustScore: Math.floor(Math.random() * 40) + 60,
        responseRate: Math.floor(Math.random() * 30) + 70,
        isOnline: Math.random() > 0.5,
        memberSince: new Date(Date.now() - Math.random() * 10000000000),
      },
      postedAt: new Date(Date.now() - Math.random() * 1000000000),
      matchScore: Math.floor(Math.random() * 30) + 70,
      rarityScore: Math.floor(Math.random() * 100),
      urgencyScore: Math.floor(Math.random() * 50),
      attributes: {},
    }));

    setListings((prev) => [...prev, ...mockListings]);
    setIsLoading(false);
  };

  const handleSwipe = useCallback(
    (action: SwipeActionType) => {
      const listing = listings[currentIndex];
      if (!listing) return;

      if (action === 'save') {
        onSave(listing);
      } else if (action === 'share') {
        onShare(listing);
      }

      // Move to next card
      setTimeout(() => {
        setCurrentIndex((prev: number) => prev + 1);
        if (currentIndex >= listings.length - 3) {
          loadMoreListings();
        }
      }, 300);
    },
    [currentIndex, listings, onSave, onShare]
  );

  const handleUndo = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev: number) => prev - 1);
    }
  };

  // Render visible cards (current + next 2)
  const visibleCards = listings.slice(currentIndex, currentIndex + 3);

  return (
    <div className="relative w-full h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-dark-100">
        <h2 className="text-lg font-bold text-dark-900">Discover</h2>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="p-2 hover:bg-dark-50 rounded-full transition-colors"
        >
          <SlidersHorizontal className="w-5 h-5 text-dark-600" />
        </button>
      </div>

      {/* Filters panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-dark-50 border-b border-dark-100 overflow-hidden"
          >
            <div className="p-4 space-y-3">
              {/* Category chips */}
              <div className="flex flex-wrap gap-2">
                {['All', 'Electronics', 'Vehicles', 'Fashion', 'Furniture'].map((cat) => (
                  <button
                    key={cat}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      cat === 'All'
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-dark-600 border border-dark-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Price range */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-dark-500">Price:</span>
                <select className="text-sm bg-white border border-dark-200 rounded-lg px-3 py-1.5">
                  <option>Any</option>
                  <option>Under 10K</option>
                  <option>10K - 50K</option>
                  <option>50K - 100K</option>
                  <option>100K+</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Card stack */}
      <div className="flex-1 relative p-4">
        {visibleCards.length > 0 ? (
          <div className="relative w-full h-full max-w-md mx-auto">
            {visibleCards.map((listing, index) => (
              <DiscoveryCard
                key={listing.id}
                listing={listing}
                isActive={index === 0}
                zIndex={3 - index}
                style={{
                  transform: `scale(${1 - index * 0.05}) translateY(${index * 10}px)`,
                }}
                onSwipe={handleSwipe}
                onTap={() => onListingClick(listing)}
                onDoubleTap={() => handleSwipe('like')}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mb-4">
              <MapPin className="w-10 h-10 text-primary-500" />
            </div>
            <h3 className="text-lg font-bold text-dark-900 mb-2">No more listings</h3>
            <p className="text-dark-500 text-sm mb-4">
              We've shown you all available items. Adjust your filters or check back later!
            </p>
            <button
              onClick={() => {
                setCurrentIndex(0);
                setListings([]);
                loadMoreListings();
              }}
              className="btn-primary"
            >
              Refresh Feed
            </button>
          </div>
        )}
      </div>

      {/* Stats bar */}
      <div className="px-4 py-3 bg-white border-t border-dark-100">
        <div className="flex items-center justify-between text-sm text-dark-500">
          <span>{currentIndex} items seen</span>
          {currentIndex > 0 && (
            <button
              onClick={handleUndo}
              className="text-primary-600 font-medium hover:text-primary-700"
            >
              Undo last swipe
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
